import { PublicEventDetails } from "@ac-expo/api-types";
import { BuyableEntryTypeWithKey, UseLocalCartReturns } from "@ac-expo/hooks";
import { isGroupPurchaseMinimumUnmet } from "@ac-expo/utils";
import { Warning } from "@phosphor-icons/react";
import {
  GROUP_TICKETS_TITLE,
  GroupUnlock,
  GroupUnlockedSession,
  isGroupTicketType,
} from "@web-app/src/components/Ticket/GroupUnlock";
import { TicketingList } from "@web-app/src/components/Ticket/TicketingList";
import { useMemo } from "react";
import { EmbedNavigation } from "./EmbedNavigation";
import { STEPS } from "./EmbedTicketing";
import TotalCart from "./TotalCart";

type EmbedCartComponentProps = {
  setStep: (step: STEPS) => void;
  event: PublicEventDetails;
  onGroupUnlocked: (session: GroupUnlockedSession) => Promise<void>;
  localCart: UseLocalCartReturns;
  buyableTicketTypes: BuyableEntryTypeWithKey<"ticket">[];
  buyableBadgeTypes: BuyableEntryTypeWithKey<"badge">[];
  buyableActivityTicketTypes: BuyableEntryTypeWithKey<"activity">[];
  totalPrice: number;
  totalDiscount: number;
  isDisabled: boolean;
};

export function EmbedCartComponent({
  setStep,
  event,
  onGroupUnlocked,
  localCart,
  buyableTicketTypes,
  buyableBadgeTypes,
  buyableActivityTicketTypes,
  totalPrice,
  totalDiscount,
  isDisabled,
}: EmbedCartComponentProps) {
  const { getTotalQuantity } = localCart;

  // Group tickets are only returned to their members: list them apart from the regular prices
  const { regularTicketTypes, groupTicketTypes } = useMemo(
    () => ({
      regularTicketTypes: buyableTicketTypes.filter(
        (entry) => !isGroupTicketType(entry.entryType),
      ),
      groupTicketTypes: buyableTicketTypes.filter((entry) => isGroupTicketType(entry.entryType)),
    }),
    [buyableTicketTypes],
  );

  // No session cookie reaches the iframe, so the visitor is anonymous until they unlock
  const canUnlockGroupTickets = event.hasGroupTicketTypes && groupTicketTypes.length === 0;

  const ticketsQuantity = useMemo(
    () => buyableTicketTypes.reduce((acc, entry) => acc + getTotalQuantity(entry.itemKey), 0),
    [buyableTicketTypes, getTotalQuantity],
  );

  const activityTicketsQuantity = useMemo(
    () =>
      buyableActivityTicketTypes.reduce((acc, entry) => acc + getTotalQuantity(entry.itemKey), 0),
    [buyableActivityTicketTypes, getTotalQuantity],
  );

  const badgesQuantity = useMemo(
    () => buyableBadgeTypes.reduce((acc, entry) => acc + getTotalQuantity(entry.itemKey), 0),
    [buyableBadgeTypes, getTotalQuantity],
  );

  // Same rule as the site: the minimum is stated once under the line, and blocks the step here.
  // Badges carry the same minimum as tickets, and the server refuses both.
  const hasGroupMinSizeError = useMemo(
    () =>
      [...buyableTicketTypes, ...buyableActivityTicketTypes, ...buyableBadgeTypes].some((entry) =>
        isGroupPurchaseMinimumUnmet(entry.entryType, getTotalQuantity(entry.itemKey)),
      ),
    [buyableTicketTypes, buyableActivityTicketTypes, buyableBadgeTypes, getTotalQuantity],
  );

  const isNextStepDisabled =
    (badgesQuantity === 0 && ticketsQuantity === 0 && activityTicketsQuantity === 0) ||
    hasGroupMinSizeError;

  const isMissingTickets = ticketsQuantity < activityTicketsQuantity;

  return (
    <div className="flex flex-col">
      <div className="flex size-full flex-1 flex-col gap-2 overflow-y-auto">
        <TicketingList
          type="ticket"
          buyableEntries={regularTicketTypes}
          localCart={localCart}
          buyer={event.buyer}
        />
        <TicketingList
          type="ticket"
          title={GROUP_TICKETS_TITLE}
          buyableEntries={groupTicketTypes}
          localCart={localCart}
          buyer={event.buyer}
        />
        {canUnlockGroupTickets && (
          <GroupUnlock event={event} onUnlocked={onGroupUnlocked} canLogin={false} />
        )}
        <TicketingList type="badge" buyableEntries={buyableBadgeTypes} localCart={localCart} />
        <TicketingList
          type="activity"
          buyableEntries={buyableActivityTicketTypes}
          localCart={localCart}
          buyer={event.buyer}
        />
      </div>
      <TotalCart totalPrice={totalPrice} totalDiscount={totalDiscount} />
      {isMissingTickets && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2">
          <div>
            <Warning color="orange" size={24} />
          </div>
          <p className="text-xs italic text-gray-500">
            ATTENTION, vous avez ajouté {activityTicketsQuantity}{" "}
            {activityTicketsQuantity > 1 ? "billets" : "billet"} d&apos;animation dans votre panier.
            Vous devez également ajouter {activityTicketsQuantity - ticketsQuantity} billet
            {activityTicketsQuantity - ticketsQuantity > 1 ? "s" : ""} d&apos;entrée pour pouvoir
            accéder à l&apos;événement
          </p>
        </div>
      )}

      <EmbedNavigation
        setNextStep={() => setStep("INFORMATIONS")}
        isNextStepDisabled={isNextStepDisabled}
        hideNextStep={isDisabled}
      />
    </div>
  );
}
